<template>
    <div v-if="profile" class="VolunteerId">
        <VolunteerView :profile="profile" />

        <RecommendedItems v-if="isProd" :id="profile.id" collection="volunteers">
            {{ recommendedListHeading }}
        </RecommendedItems>
    </div>
</template>

<script setup lang="ts">
import type { UserProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'
import { getFullName } from '#shared/utils'
import { OverviewType } from '#shared/types/Overview'
import { useJsonLd } from '@/composables/head/useJsonLd'
import { useSharing } from '@/composables/sharing/useSharing'
import { isProduction } from '@/utils/helpers'
import {
    absoluteUrl,
    buildBreadcrumbList,
    buildProfilePage,
    collapseWhitespace,
    composeDescription,
    getOngoingJob,
    htmlToPlainText,
    SECTION_SOCIAL_IMAGES,
    toIsoDateTime,
    withSiteName
} from '@/utils/structuredData'

const route = useRoute()
const store = useApplicationStore()
const { t: $t } = useI18n()

const isProd = isProduction()

// Composables
const { getSharingUrl } = useSharing()

// Data from route params
const volunteerId = route.params.id as string

// Fetch data using server API (works on both SSR and client)
const { data, error, status } = await useFetch<UserProfile>(`/api/profiles/${volunteerId}`, {
    key: `volunteer-profile-${volunteerId}`
})

// Profile from fetch result
const profile = computed(() => data.value || null)

// Validate volunteer profile
if (profile.value) {
    // Check if volunteerProfile visibility exists
    if (profile.value.volunteerProfile && !profile.value.volunteerProfile.visibility) {
        throw createError({
            statusCode: 404,
            message: 'Volunteer profile does not exist.'
        })
    }

    // Handle hidden visibility redirect (client-side only for owner check)
    if (profile.value.volunteerProfile?.visibility === VisibilityType.hidden) {
        const isOwner = import.meta.client ? store.userId === profile.value.id : true
        if (!isOwner) {
            navigateTo({ path: '/ponuka-nie-je-aktualne-dostupna',
                query: {
                    type: OverviewType.VOLUNTEERS,
                    id: volunteerId
                }
            })
        }
    }
}

// Handle error
if (error.value) {
    throw createError({
        statusCode: 404,
        message: error.value.message || 'Volunteer not found'
    })
}

// Computed properties
const volunteerFullName = computed(() => {
    return profile.value ? collapseWhitespace(getFullName(profile.value)) : ''
})

// Hidden profiles render a "not active" placeholder (see VolunteerView) and a missing
// volunteer profile has nothing to show, so neither may expose the person in the head.
const isPublicVolunteer = computed(() => {
    const visibility = profile.value?.volunteerProfile?.visibility
    return visibility === VisibilityType.published || visibility === VisibilityType.unlisted
})

if (import.meta.server && profile.value && !isPublicVolunteer.value) {
    setResponseStatus(404)
}

// Unlisted profiles are shared by link only: they render, but stay out of search and markup.
const isIndexableVolunteer = computed(() => profile.value?.volunteerProfile?.visibility === VisibilityType.published)

const volunteerInterests = computed(() => profile.value?.volunteerProfile?.interests ?? [])
const volunteerBio = computed(() => htmlToPlainText(profile.value?.shortProfile || profile.value?.hobby))

const volunteerDescription = computed(() => {
    if (volunteerBio.value) return composeDescription([volunteerBio.value])
    const interests = volunteerInterests.value.slice(0, 5).join(', ')
    return composeDescription([
        `${volunteerFullName.value} – dobrovoľnícky profil na GROWNi.`,
        interests ? `S čím vie pomôcť: ${interests}.` : ''
    ])
})

const volunteerPageTitle = computed(() => {
    return isPublicVolunteer.value ? withSiteName(`${volunteerFullName.value} – dobrovoľník/čka`) : withSiteName('Dobrovoľnícky profil')
})

const volunteerImage = computed(() => {
    return profile.value?.profileImg ? getSharingUrl('volunteers', profile.value.profileImg) : SECTION_SOCIAL_IMAGES.volunteers
})

const canonicalUrl = computed(() => absoluteUrl(`/dobrovolnictvo/dobrovolnici/${profile.value?.slug || volunteerId}`))

const recommendedListHeading = computed(() => {
    return $t('relatedItems.volunteer.headline', 'sk') as string
})

// Only what VolunteerView renders: name, photo, short bio, current job and the help tags.
const profilePageJsonLd = computed(() => {
    if (!profile.value || !isIndexableVolunteer.value) return null
    const ongoingJob = getOngoingJob(profile.value.jobs)
    return buildProfilePage({
        name: volunteerFullName.value,
        url: canonicalUrl.value,
        image: profile.value.profileImg,
        description: htmlToPlainText(profile.value.shortProfile || profile.value.hobby, 250),
        jobTitle: ongoingJob?.position,
        worksFor: ongoingJob?.org,
        knowsAbout: volunteerInterests.value,
        dateModified: toIsoDateTime(profile.value.volunteerProfile?.updatedAt)
    })
})

useJsonLd(() => [
    profilePageJsonLd.value,
    isIndexableVolunteer.value &&
        buildBreadcrumbList([
            { name: 'Domov', path: '/' },
            { name: 'Dobrovoľníctvo', path: '/dobrovolnictvo' },
            { name: 'Dobrovoľníci', path: '/dobrovolnictvo/dobrovolnici' },
            { name: volunteerFullName.value, path: canonicalUrl.value }
        ])
])

// Head meta
useHead(() => ({
    title: volunteerPageTitle.value,
    link: [
        // `key` dedupes against the site-wide canonical set in app.vue
        { rel: 'canonical', key: 'canonical', href: canonicalUrl.value }
    ],
    meta: isPublicVolunteer.value
        ? [
              ...(isIndexableVolunteer.value ? [] : [{ name: 'robots', content: 'noindex, follow' }]),
              { name: 'description', content: volunteerDescription.value },
              { property: 'og:title', content: volunteerPageTitle.value },
              { property: 'og:description', content: volunteerDescription.value },
              { property: 'og:image', content: volunteerImage.value },
              { property: 'og:image:alt', content: `Profilová fotka: ${volunteerFullName.value}` },
              { property: 'og:type', content: 'profile' },
              { property: 'og:url', content: canonicalUrl.value }
          ]
        : [{ name: 'robots', content: 'noindex, follow' }],
    script: [
        {
            key: 'gtm-volunteer-page',
            innerHTML: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'unique_id': '${profile.value?.id || ''}'
        });
      `,
            type: 'text/javascript'
        }
    ]
}))

// Note: Dynamic title event emission removed - not available in Nuxt 3
// If needed, use a store or composable to manage dynamic titles
</script>
